/**
 * Equipe e distribuição de atendimentos (Prompt 01): formatos compartilhados
 * entre servidor e interface.
 */
import { z } from "zod";
import {
  PAPEIS,
  STATUS_PRESENCA,
  type Papel,
  type StatusPresenca,
  type TipoAtribuicao,
} from "./dominio";

/** "HH:MM" 00:00–23:59. */
const hora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use o formato HH:MM.");

/** Chave = dia da semana (0 domingo ... 6 sábado); valor = faixas [início, fim]. */
export const horarioTrabalhoSchema = z
  .record(z.enum(["0", "1", "2", "3", "4", "5", "6"]), z.array(z.tuple([hora, hora])).max(4))
  .refine((h) => Object.values(h).every((faixas) => faixas.every(([a, b]) => a < b)), {
    message: "O fim de cada faixa precisa ser depois do início.",
  });
export type HorarioTrabalho = z.infer<typeof horarioTrabalhoSchema>;

export const DIAS_SEMANA = [
  "Domingo",
  "Segunda",
  "Terça",
  "Quarta",
  "Quinta",
  "Sexta",
  "Sábado",
] as const;

export const ESTRATEGIAS_DISTRIBUICAO = ["menor_carga", "rodizio"] as const;
export type EstrategiaDistribuicao = (typeof ESTRATEGIAS_DISTRIBUICAO)[number];

export const ROTULO_ESTRATEGIA: Record<EstrategiaDistribuicao, string> = {
  menor_carga: "Quem tem menos atendimentos",
  rodizio: "Rodízio (uma de cada vez)",
};

export const configDistribuicaoSchema = z.object({
  /** Desligado: toda conversa nova fica na fila até alguém assumir. */
  ativa: z.boolean().default(true),
  estrategia: z.enum(ESTRATEGIAS_DISTRIBUICAO).default("menor_carga"),
  /** Só distribui para quem está "Disponível". */
  respeitarPresenca: z.boolean().default(true),
  /** Só distribui dentro do horário de trabalho de cada vendedora. */
  respeitarHorario: z.boolean().default(true),
  /** Cliente com vendedora preferencial vai direto para ela, se disponível. */
  preferirVendedoraDoCliente: z.boolean().default(true),
  /** Cliente que volta é atendido por quem a atendeu da última vez. */
  preferirAtendimentoAnterior: z.boolean().default(true),
  /** Máximo de atendimentos abertos por vendedora (cada uma pode ter o seu). */
  limitePadrao: z.number().int().min(1).max(200).default(15),
  /** Pergunta à cliente nova com qual vendedora ela quer falar. */
  perguntarVendedora: z.boolean().default(false),
  /** Minutos esperando resposta antes de escalonar. 0 = nunca. */
  escalonarAposMin: z
    .number()
    .int()
    .min(0)
    .max(24 * 60)
    .default(15),
  /** "sugerir": avisa a gerência. "redistribuir": passa para outra vendedora. */
  acaoEscalonamento: z.enum(["sugerir", "redistribuir"]).default("sugerir"),
});
export type ConfigDistribuicao = z.infer<typeof configDistribuicaoSchema>;
export const CONFIG_DISTRIBUICAO_PADRAO: ConfigDistribuicao = configDistribuicaoSchema.parse({});

export interface UsuarioDetalhe {
  id: string;
  nome: string;
  email: string;
  papel: Papel;
  ativo: boolean;
  statusPresenca: StatusPresenca;
  vistoEm: string | null;
  erpVendedorId: string | null;
  recebeDistribuicao: boolean;
  maxAtendimentos: number | null;
  horarioTrabalho: HorarioTrabalho | null;
  empresaIds: string[];
  atendimentosAbertos: number;
  aguardandoResposta: number;
  /** Desde quando a cliente que espera há mais tempo está esperando. */
  esperaMaisAntiga: string | null;
  ultimoAcessoEm: string | null;
}

const camposUsuario = {
  nome: z.string().trim().min(2, "Informe o nome.").max(80),
  papel: z.enum(PAPEIS),
  erpVendedorId: z.string().trim().max(100).nullish(),
  recebeDistribuicao: z.boolean(),
  maxAtendimentos: z.number().int().min(1).max(200).nullish(),
  horarioTrabalho: horarioTrabalhoSchema.nullish(),
  empresaIds: z.array(z.string().uuid()).min(1, "Escolha ao menos uma empresa."),
};

export const novoUsuarioSchema = z.object({
  ...camposUsuario,
  email: z.string().trim().toLowerCase().email("E-mail inválido."),
  recebeDistribuicao: z.boolean().default(true),
});
export type NovoUsuario = z.infer<typeof novoUsuarioSchema>;

export const editarUsuarioSchema = z.object({ ...camposUsuario, ativo: z.boolean() }).partial();
export type EditarUsuario = z.infer<typeof editarUsuarioSchema>;

export const trocarSenhaSchema = z.object({
  atual: z.string().min(1, "Informe a senha atual."),
  nova: z.string().min(10, "A nova senha precisa ter pelo menos 10 caracteres.").max(200),
});

export const presencaSchema = z.object({ status: z.enum(STATUS_PRESENCA) });

export const transferirSchema = z.object({
  usuarioId: z.string().uuid(),
  motivo: z.string().trim().max(200).nullish(),
});
export const retirarSchema = z.object({
  motivo: z.string().trim().max(200).nullish(),
  /** Já tenta passar para outra vendedora pela distribuição automática. */
  redistribuir: z.boolean().default(false),
});
export const prioridadeSchema = z.object({ prioridade: z.number().int().min(0).max(2) });

export const ROTULO_PRIORIDADE = ["Normal", "Alta", "Urgente"] as const;

export interface AtribuicaoDto {
  id: string;
  tipo: TipoAtribuicao;
  de: { id: string; nome: string } | null;
  para: { id: string; nome: string } | null;
  por: { id: string; nome: string } | null;
  motivo: string | null;
  criadoEm: string;
}

export const ROTULO_TIPO_ATRIBUICAO: Record<TipoAtribuicao, string> = {
  automatica: "Distribuição automática",
  manual: "Atribuída",
  transferencia: "Transferida",
  retirada: "Devolvida à fila",
  escalonamento: "Escalonada",
};

/** Motivos gravados pelo sistema, em linguagem de gente. */
export const ROTULO_MOTIVO: Record<string, string> = {
  vendedora_preferencial: "vendedora preferencial da cliente",
  atendimento_anterior: "atendeu a cliente da última vez",
  menor_carga: "menos atendimentos no momento",
  rodizio: "rodízio",
  prioridade: "cliente prioritária",
  escolha_cliente: "escolhida pela cliente",
  respondeu_primeiro: "respondeu primeiro",
  assumiu: "assumiu da fila",
  espera_excessiva: "cliente esperando além do limite",
  responsavel_indisponivel: "responsável indisponível",
  usuario_desativado: "usuária desativada",
  redistribuicao: "redistribuição pela gerência",
  automacao: "regra de automação",
};

export const TIPOS_ALERTA = [
  "espera_excessiva",
  "responsavel_indisponivel",
  "fila_sem_vendedora",
  "sla_estourado",
  "ponte_limite",
  "automacao",
] as const;
export type TipoAlerta = (typeof TIPOS_ALERTA)[number];

export interface AlertaDto {
  id: string;
  tipo: TipoAlerta;
  mensagem: string;
  conversa: { id: string; contatoNome: string; aguardandoDesde: string | null } | null;
  responsavel: { id: string; nome: string } | null;
  sugestao: { id: string; nome: string } | null;
  criadoEm: string;
}
